import type { SessionGrant } from '@aithora/shared';

// Sign-in pieces the Flutter console had (login_page.dart): single sign-on's return trip,
// and "Trust this device for 30 days".

const SSO_KEY = 'aithora.ssoReturn';
const TRUST_PREFIX = 'aithora.trustDevice.';

/** Words for the server's sso_error codes (router.ts /auth/oauth/:provider/callback). */
const SSO_ERRORS: Record<string, string> = {
  not_provisioned: 'That account is not on Aithora yet. Ask your administrator for an invite.',
  account_inactive: 'That account is not active. Ask your administrator.',
  email_unverified: 'Your provider has not verified that email address.',
  provider_disabled: 'That sign-in option is switched off.',
  bad_state: 'The sign-in took too long or was interrupted. Try again.',
  exchange_failed: 'Your provider did not complete the sign-in. Try again.',
  no_email: 'Your provider did not share an email address.',
  server_error: 'Sign-in failed on our side. Try again.',
};

/**
 * The SSO callback lands on the console with the session in the URL fragment
 * (#session=...&role=... or #sso_error=...). Read it once at start-up, keep it for the
 * sign-in page, and take it out of the address bar and history straight away.
 */
export function captureSsoReturn(loc: Location = window.location, store: Storage = sessionStorage): void {
  const h = loc.hash.replace(/^#/, '');
  if (!h) return;
  const p = new URLSearchParams(h);
  if (!p.has('session') && !p.has('sso_error')) return;
  try {
    store.setItem(SSO_KEY, h);
  } catch {
    /* storage refused: the person signs in again */
  }
  try {
    window.history.replaceState(null, '', loc.pathname + loc.search);
  } catch {
    /* not in a browser */
  }
}

export type SsoReturn = { kind: 'grant'; grant: SessionGrant } | { kind: 'error'; message: string } | null;

/** The SSO result kept by captureSsoReturn, once. */
export function takeSsoReturn(store: Storage = sessionStorage): SsoReturn {
  let h: string | null = null;
  try {
    h = store.getItem(SSO_KEY);
    store.removeItem(SSO_KEY);
  } catch {
    return null;
  }
  if (!h) return null;
  const p = new URLSearchParams(h);
  const err = p.get('sso_error');
  if (err) return { kind: 'error', message: SSO_ERRORS[err] ?? 'Single sign-on did not complete. Try again.' };
  const token = p.get('session');
  const userId = p.get('uid');
  const role = p.get('role');
  if (!token || !userId || !role) return null;
  return { kind: 'grant', grant: { ok: true, token, userId, role, name: p.get('name') || null, organizationId: p.get('org') || null, expiresAt: '' } };
}

const trustKey = (who: string) => TRUST_PREFIX + who.trim().toLowerCase();

/** The device-trust token saved for this username or email, if any. */
export function readTrustToken(who: string): string | undefined {
  try {
    return localStorage.getItem(trustKey(who)) ?? undefined;
  } catch {
    return undefined;
  }
}

/** Keep (or forget) this device's trust token for this username or email. */
export function writeTrustToken(who: string, token: string | null | undefined): void {
  try {
    if (token) localStorage.setItem(trustKey(who), token);
    else localStorage.removeItem(trustKey(who));
  } catch {
    /* storage refused: the second step is simply asked again */
  }
}
